// src/Components/TopBar/SubscribeDialog.js

import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  Alert,
  Collapse,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { signInWithPopup, signOut } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, provider, db, functions } from '../../Firebase/firebase';
import GooogleImage from '../../assets/images/Google-G.png';

const theme = createTheme();

export default function SubscribeDialog({ open, onClose }) {
  const [step, setStep] = useState('INPUT');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [otp, setOtp] = useState('');

  const [loadingAction, setLoadingAction] = useState(null);
  const [alert, setAlert] = useState({ visible: false, severity: '', message: '' });
  const timerRef = useRef(null);

  // Clear fields when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setStep('INPUT');
      setEmail('');
      setName('');
      setCompany('');
      setOtp('');
      setAlert({ visible: false, severity: '', message: '' });
      setLoadingAction(null);
    }
  }, [open]);

  const showAlert = (severity, message) => {
    setAlert({ visible: true, severity, message });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAlert({ visible: false, severity: '', message: '' });
    }, 4000);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      showAlert('error', 'Please provide both Name and Email');
      return;
    }
    setLoadingAction('EMAIL');
    try {
      const requestSubscribeOtp = httpsCallable(functions, 'requestSubscribeOtp');
      await requestSubscribeOtp({
        email: email.toLowerCase().trim(),
        userName: name.trim(),
        companyName: company.trim(),
      });

      setLoadingAction(null);
      setStep('OTP');
      showAlert('success', 'Verification code sent to your email!');
    } catch (err) {
      console.error('OTP Request Error:', err);
      showAlert(
        'error',
        err.message || 'Failed to send verification code. Please try again.',
      );
      setLoadingAction(null);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      showAlert('error', 'Please enter the 6-digit code');
      return;
    }
    setLoadingAction('VERIFY');
    try {
      const verifySubscribeOtp = httpsCallable(functions, 'verifySubscribeOtp');
      await verifySubscribeOtp({
        email: email.toLowerCase().trim(),
        otpCode: otp.trim(),
      });

      showAlert('success', 'Successfully subscribed!');
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error('OTP Verification Error:', err);
      showAlert('error', 'Invalid or expired code. Please try again.');
      setLoadingAction(null);
    }
  };

  const handleGoogleSubscribe = async () => {
    setLoadingAction('GOOGLE');
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const safeEmailId = user.email.toLowerCase().trim();
      const dateNow = new Date().toISOString();
      const finalUserName = user.displayName || name.trim() || '';

      const mailingRef = doc(db, 'mailingList', user.uid);

      // We only insert into mailingList for Guest Subscriptions and merge:true updates existing details
      await setDoc(
        mailingRef,
        {
          addedAt: dateNow,
          companyName: company.trim(),
          email: safeEmailId,
          userName: finalUserName,
        },
        { merge: true },
      );

      // Instantly sign them back out
      await signOut(auth);

      showAlert('success', 'Successfully subscribed with Google!');
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setLoadingAction(null);
    }
  };

  const inputStyle = {
    '& .MuiInputBase-root': { height: 40 },
    '& .MuiInputLabel-root': { transform: 'translate(14px, 10px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' },
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={loadingAction ? undefined : onClose}
        maxWidth='xs'
        fullWidth
        sx={{ zIndex: 100050 }}
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'visible', position: 'relative' },
        }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } }}>
        <IconButton
          onClick={onClose}
          disabled={loadingAction !== null}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '100%' }}>
            <Collapse in={alert.visible}>
              <Alert severity={alert.severity} sx={{ mb: 2 }}>
                {alert.message}
              </Alert>
            </Collapse>
          </Box>

          <Typography component='h1' variant='h5' fontWeight='bold' sx={{ mb: 1 }}>
            {step === 'INPUT' ? 'Subscribe to Newsfeed' : 'Verify your Email'}
          </Typography>

          {step === 'INPUT' ? (
            <>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 3, textAlign: 'center' }}>
                Get the latest updates sent directly to your inbox.
              </Typography>

              <Box
                component='form'
                noValidate
                onSubmit={handleRequestOTP}
                sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label='Name'
                  size='small'
                  margin='dense'
                  fullWidth
                  required
                  autoFocus
                  disabled={loadingAction !== null}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={inputStyle}
                />
                <TextField
                  label='Company (Optional)'
                  size='small'
                  margin='dense'
                  fullWidth
                  disabled={loadingAction !== null}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  sx={inputStyle}
                />
                <TextField
                  label='Email Address'
                  type='email'
                  size='small'
                  margin='dense'
                  fullWidth
                  required
                  disabled={loadingAction !== null}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={inputStyle}
                />
                <Button
                  type='submit'
                  variant='contained'
                  disabled={loadingAction !== null}
                  sx={{
                    mt: 2,
                    mb: 1,
                    py: 1,
                    height: '36px',
                    backgroundColor: '#1976d2',
                    textTransform: 'none',
                    fontSize: '16px',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                      transform: 'scale(1.02)',
                    },
                    '&:active': {
                      backgroundColor: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#1976d2',
                      color: '#fff',
                    },
                  }}>
                  {loadingAction === 'EMAIL' && (
                    <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                  )}
                  <span>{loadingAction === 'EMAIL' ? '' : 'Send Verification Code'}</span>
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography
                  variant='body2'
                  sx={{ px: 2, color: 'text.secondary', fontWeight: 500 }}>
                  OR
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>

              <Button
                onClick={handleGoogleSubscribe}
                fullWidth
                variant='outlined'
                disabled={loadingAction !== null}
                sx={{
                  mb: 1,
                  backgroundColor: '#FFFFFF',
                  color: '#3c4043',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '16px',
                  borderRadius: '4px',
                  borderColor: '#dadce0',
                  paddingY: 0,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.2,
                  '&:hover': {
                    backgroundColor: '#f7f8f8',
                    borderColor: '#d2d2d2',
                  },
                  '&:active': {
                    backgroundColor: '#FFFFFF',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#FFFFFF',
                    borderColor: '#dadce0',
                    color: '#3c4043',
                  },
                }}>
                {loadingAction === 'GOOGLE' ? (
                  <CircularProgress size={20} sx={{ color: '#3c4043' }} />
                ) : (
                  <img
                    src={GooogleImage}
                    alt='Google'
                    style={{ width: 40, height: 40, objectFit: 'contain' }}
                  />
                )}
                <span>Subscribe with Google</span>
              </Button>
            </>
          ) : (
            <Box
              component='form'
              onSubmit={handleVerifyOTP}
              sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 1, textAlign: 'center' }}>
                We sent a 6-digit code to <strong>{email}</strong>. Please enter it below
                to confirm your subscription.
              </Typography>

              <TextField
                label='6-Digit Code'
                size='small'
                margin='dense'
                fullWidth
                required
                autoFocus
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    letterSpacing: '0.2em',
                    fontWeight: 'bold',
                  },
                }}
                disabled={loadingAction !== null}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} // Numbers only
                sx={inputStyle}
              />

              <Button
                type='submit'
                variant='contained'
                disabled={loadingAction !== null || otp.length < 6}
                sx={{
                  mt: 2,
                  mb: 1,
                  py: 1,
                  height: '36px',
                  backgroundColor: '#1976d2',
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    transform: 'scale(1.02)',
                  },
                  '&:active': {
                    backgroundColor: '#1976d2',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#1976d2',
                    color: '#fff',
                  },
                }}>
                {loadingAction === 'VERIFY' && (
                  <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                )}
                <span>{loadingAction === 'VERIFY' ? '' : 'Verify & Subscribe'}</span>
              </Button>

              <Button
                variant='text'
                onClick={() => setStep('INPUT')}
                disabled={loadingAction !== null}
                sx={{ textTransform: 'none', mt: 1 }}>
                Change Email Address
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
